import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, mediaQueryLarge } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #previousScrollTop = 0;
  #activeCategory = null;
  #categoryTrigger = null;
  #rootScrollTop = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown);
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    if (this.isOpen) removeTrapFocus();
  }

  #onBreakpointChange = () => {
    if (mediaQueryLarge.matches && this.isOpen) this.close();
  };

  #openOverlay(dialog, show) {
    if (!dialog) return;
    removeTrapFocus();
    dialog.addEventListener('close', () => {
      if (this.isOpen) this.#focusPanel(this.refs.menuDrawer);
    }, { once: true });
    show();
  }

  openSearch() {
    const search = document.getElementById('search-modal');
    this.#openOverlay(search?.querySelector('dialog'), () => search.showDialog());
  }

  openSaved() {
    this.#openOverlay(document.getElementById('reevys-bookmarks-dialog'), () => {
      document.querySelector('.reevys-bookmark')?.click();
    });
  }

  #focusPanel(panel) {
    trapFocus(panel);
    scrollTo({ top: this.#previousScrollTop, behavior: 'instant' });
  }

  openCategory(id, event) {
    const panel = document.getElementById(id);
    if (!panel || !this.contains(panel) || !panel.hasAttribute('data-menu-panel')) return;
    this.#categoryTrigger = event?.target.closest('[data-menu-category]');
    this.#rootScrollTop = this.refs.menuDrawer.scrollTop;
    this.#activeCategory = panel;
    this.#categoryTrigger?.setAttribute('aria-expanded', 'true');
    this.querySelectorAll('[data-menu-root], .reevys-mobile-menu-toolbar, .reevys-mobile-menu-footer').forEach(element => {
      element.hidden = true;
      element.inert = true;
    });
    panel.hidden = false;
    panel.inert = false;
    panel.querySelector('.reevys-mobile-drilldown__body').scrollTop = 0;
    this.refs.menuDrawer.classList.add('menu-drawer--drilldown');
    this.refs.menuDrawer.scrollTop = 0;
    this.#focusPanel(panel);
    panel.querySelector('button')?.focus({ preventScroll: true });
  }

  backCategory() {
    this.#resetCategory(true);
  }

  #resetCategory(restoreFocus = false) {
    if (!this.#activeCategory) return;
    this.#activeCategory.hidden = true;
    this.#activeCategory.inert = true;
    this.#activeCategory = null;
    this.#categoryTrigger?.setAttribute('aria-expanded', 'false');
    this.querySelectorAll('[data-menu-root], .reevys-mobile-menu-toolbar, .reevys-mobile-menu-footer').forEach(element => {
      element.hidden = false;
      element.inert = false;
    });
    this.refs.menuDrawer.classList.remove('menu-drawer--drilldown');
    this.refs.menuDrawer.scrollTop = this.#rootScrollTop;
    if (restoreFocus) {
      this.#focusPanel(this.refs.menuDrawer);
      this.#categoryTrigger?.focus({ preventScroll: true });
    }
    this.#categoryTrigger = null;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Tab' && this.isOpen) {
      const panel = this.#activeCategory || this.refs.menuDrawer;
      const focusable = [...panel.querySelectorAll('a[href], button:not(:disabled), summary, input, select, [tabindex="0"]')]
        .filter(element => element.getClientRects().length && !element.closest('[inert]'));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first && last && ((event.shiftKey && (document.activeElement === first || document.activeElement === panel)) || (!event.shiftKey && document.activeElement === last))) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      }
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#activeCategory) this.backCategory();
    else this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;
    if (details === this.refs.details) this.#previousScrollTop = getScrollTop();

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (this.isOpen && details.classList.contains('menu-open')) this.#focusPanel(drawer || details);
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          this.#resetCategory();
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          this.#focusPanel(this.refs.menuDrawer);
        }
        summary.focus({ preventScroll: true });
        scrollTo({ top: this.#previousScrollTop, behavior: 'instant' });
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
